import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../data/translations';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Truck, Package, Award, DollarSign, HeadphonesIcon, ShieldCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const services = [
    {
      icon: Truck,
      title: t('services.service1Title'),
      description: t('services.service1Text'),
      image: 'https://images.pexels.com/photos/6969848/pexels-photo-6969848.jpeg'
    },
    {
      icon: Package,
      title: t('services.service2Title'),
      description: t('services.service2Text'),
      image: 'https://images.unsplash.com/photo-1617347454431-f49d7ff5c3b1'
    },
    {
      icon: Award,
      title: t('services.service3Title'),
      description: t('services.service3Text'),
      image: 'https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg'
    },
    {
      icon: ShieldCheck,
      title: t('services.service4Title'),
      description: t('services.service4Text'),
      image: 'https://images.unsplash.com/photo-1589109807644-924edf14ee09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NzQyNTQwMzl8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: DollarSign,
      title: t('services.service5Title'),
      description: t('services.service5Text'),
      image: 'https://images.pexels.com/photos/5779775/pexels-photo-5779775.jpeg'
    },
    {
      icon: HeadphonesIcon,
      title: t('services.service6Title'),
      description: t('services.service6Text'),
      image: 'https://images.pexels.com/photos/7640434/pexels-photo-7640434.jpeg'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a3a52] to-[#2a4a62]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t('services.title')}
          </h1>
          <p className="text-xl text-gray-200">
            {t('services.subtitle')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card
                  key={index}
                  className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white"
                >
                  <div
                    className="h-48 bg-cover bg-center relative"
                    style={{
                      backgroundImage: `linear-gradient(rgba(26, 58, 82, 0.7), rgba(26, 58, 82, 0.7)), url('${service.image}')`
                    }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-[#c5a365] rounded-full w-20 h-20 flex items-center justify-center">
                        <IconComponent className="h-10 w-10 text-white" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-[#1a3a52] mb-4">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a3a52] mb-4">
              {language === 'en' ? 'How We Work' : 'كيف نعمل'}
            </h2>
            <p className="text-xl text-gray-600">
              {language === 'en' ? 'Simple and efficient process' : 'عملية بسيطة وفعالة'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: { en: 'Contact Us', ar: 'اتصل بنا' },
                description: {
                  en: 'Reach out via WhatsApp or contact form',
                  ar: 'تواصل عبر واتساب أو نموذج الاتصال'
                }
              },
              {
                step: '02',
                title: { en: 'Consultation', ar: 'استشارة' },
                description: {
                  en: 'Discuss your specific requirements',
                  ar: 'ناقش متطلباتك المحددة'
                }
              },
              {
                step: '03',
                title: { en: 'Quotation', ar: 'عرض أسعار' },
                description: {
                  en: 'Receive competitive pricing',
                  ar: 'احصل على أسعار تنافسية'
                }
              },
              {
                step: '04',
                title: { en: 'Delivery', ar: 'التسليم' },
                description: {
                  en: 'Fast and reliable delivery',
                  ar: 'التسليم السريع والموثوق'
                }
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="bg-gradient-to-br from-[#1a3a52] to-[#2a4a62] text-[#c5a365] rounded-full w-20 h-20 flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-[#1a3a52] mb-3">
                  {item.title[language]}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description[language]}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1a3a52] to-[#2a4a62]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('services.ctaTitle')}
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            {t('services.ctaText')}
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#c5a365] hover:bg-[#b39355] text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              {t('services.ctaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
